import React from 'react'
import styled from 'styled-components'
import colours from '../lib/colours'
import SectionTitle from '../components/section-title'
import CloseButton from '../components/close-button'
import BackButton from '../components/back-button'
import { MobileDeviceWidth, warningMessages } from '../lib/constants'
import FilledButton from '../components/filled-button'
import Terms from '../content/total-levy-liability-estimate-terms.mdx'
import Callout from '../components/callout'
import ExpandoControl from '../controls/expando-control'
import LevyLiability from './levy-liability'
import ConservationAreas from './conservation-areas'
import SalvageTranslocation from './salvage-translocation'
import { getParcelEstimate, getTotalEstimate } from '../utils/derivedData'
import NextSteps from './next-steps'
import NvraAreaSelectedWarning from '../content/nvra-area-selected-warning.mdx'
import CrownLandSelectedWarning from '../content/crown-land-selected-warning.mdx'
import ExistingPaymentWarning from '../content/existing-payment-warning.mdx'
import TwelvePrecinctsWarning from '../content/int-precincts-warning.mdx'
import OutsideMsaLevyAreaWarning from '../content/outside-msa-levy-area-warning.mdx'
import ConservationAreaWarning from '../content/conservation-area-warning.mdx'
import ReadMoreButton from '../components/read-more-button'
import { pdfReportFunctionAddress } from '../config/report-config'
import Spinner from '../components/spinner'
import { OpenReportErrorDialogEvent } from '../lib/messages'
import IEventedComponent, { messageTypes } from '../components/IEventedComponent'
import { getLinkWithPrefix } from '../utils/otherUtils'

const navHeight = '70'
const footerHeight = '20'
const summaryGridLineHeight = 'auto'
const sidebarGridLineWidth = 'minmax(200px, 40%)'

const DetailsContainer = styled.div`
  height: calc(90vh - 40px);
  width: calc(90vw - 80px);
  display: grid;
  grid-template-columns: [start] 1fr [end];
  grid-template-rows: [start] ${navHeight}px 1fr ${footerHeight}px [end];
  grid-template-areas:
    'nav'
    'scrollableContent'
    'footer';
  @media (max-width: ${MobileDeviceWidth}px) {
    height: calc(100vh - 40px);
    width: calc(100vw - 80px);
  }
`

const Nav = styled.div`
  grid-area: nav;
  display: flex;
  justify-content: space-between;
  margin-right: 4px;
  margin-bottom: 10px;
  @media (max-width: ${MobileDeviceWidth}px) {
    margin-right: -10px;
  }
`

const ScrollableContent = styled.div`
  grid-area: scrollableContent;
  margin: 0 -40px;
  padding: 0 40px;
  overflow: auto;
  display: grid;
  grid-template-columns: [start] 1fr ${sidebarGridLineWidth} [end];
  grid-template-rows: [start] ${summaryGridLineHeight} auto [end];
  grid-template-areas:
    'summary actions'
    'main main';
  @media (max-width: ${MobileDeviceWidth}px) {
    grid-template-columns: [start] 1fr [end];
    grid-template-rows: [start] repeat(3, auto) [end];
    grid-template-areas:
      'summary'
      'actions'
      'main';
  }
`

const Summary = styled.div`
  grid-area: summary;
  padding-top: 20px;
`

const Actions = styled.div`
  grid-area: actions;
  padding: 10px 0;
`

const Main = styled.div`
  grid-area: main;
`

const Footer = styled.div`
  grid-area: footer;
`

const ActionButtonContainer = styled.div`
  padding: 10px 0;
`

const EstimateSummaryText = styled.p`
  font-size: 38px;
  font-weight: 200;
`

const SelectedParcelsSpan = styled.span`
  font-size: 18px;
  font-weight: 800;
`

const ParcelBreakdown = styled.div`
  margin-top: 25px;
`

const ParcelCardRow = styled.p`
  margin: 0;
`

const ParcelCard = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  padding: 25px 25px 15px;
  margin: 30px 0;
  border: 1px solid ${colours.lighterPurple};
  border-radius: 3px;
  :nth-child(2) {
    margin-top: 10px;
  }
  @media (max-width: ${MobileDeviceWidth}px) {
    border: none;
    border-bottom: 1px solid ${colours.lightPurple};
    border-radius: 0;
    padding: 25px 0 15px;
    :nth-child(2) {
      margin-top: 0;
    }
    :last-child {
      border: none;
    }
  }
`

const ParcelEstimateDiv = styled.div`
  @media (max-width: ${MobileDeviceWidth}px) {
    width: 100%;
  }
`

const ParcelEstimateText = styled.p`
  font-size: 24px;
  font-weight: 200;
  text-align: right;
`

const WarningContainer = styled.div`
  margin-top: 10px;
  font-weight: 300;
`

interface IProps {
  parcelsSelected: string[]
  parcelData: any
  parcelAggregates: any
  areaData: any
  onClose: any
  setInfoPopupState: any
  rates: any
}

interface IState {
  isDownloadingReport: boolean
  showReportErrorDialog: boolean
}

const DetailsView = class extends IEventedComponent<IProps, IState> {
  state = {
    isDownloadingReport: false,
    showReportErrorDialog: true
  }

  parcelHasWarnings = (parcelSpi: string) => {
    const { parcelData } = this.props
    return (
      parcelData[parcelSpi].intNonUnpaid ||
      parcelData[parcelSpi].intCrownLand ||
      parcelData[parcelSpi].intNativeVegRegulationsArea ||
      parcelData[parcelSpi].intPrecincts ||
      (parcelData[parcelSpi].levyLand === 0 && !parcelData[parcelSpi].intPrecincts)
    )
  }

  anyParcelHasWarnings = () => {
    const { parcelData, parcelsSelected } = this.props
    return (
      parcelData &&
      parcelsSelected.length &&
      Object.keys(parcelData).filter(
        parcelSpi => parcelsSelected.indexOf(parcelSpi) !== -1 && !!this.parcelHasWarnings(parcelSpi)
      ).length > 0
    )
  }

  getNumberOfParcelWarnings = (propName: string) => {
    const { parcelData, parcelsSelected } = this.props
    if (!parcelData || !parcelsSelected.length) {
      return 0
    }
    return Object.keys(parcelData).filter(
      parcelSpi => parcelsSelected.indexOf(parcelSpi) !== -1 && !!parcelData[parcelSpi][propName]
    ).length
  }

  getNumberOfOutsideMsaParcelWarnings = () => {
    const { parcelData, parcelsSelected } = this.props
    if (!parcelData || !parcelsSelected.length) {
      return 0
    }
    return Object.keys(parcelData).filter(
      parcelSpi =>
        parcelsSelected.indexOf(parcelSpi) !== -1 &&
        parcelData[parcelSpi].levyLand === 0 &&
        !parcelData[parcelSpi].intPrecincts
    ).length
  }

  getNumberOfConservationAreaWarnings = () => {
    const { parcelData, parcelsSelected } = this.props
    if (!parcelData || !parcelsSelected.length) {
      return 0
    }
    return Object.keys(parcelData).filter(
      parcelSpi => parcelsSelected.indexOf(parcelSpi) !== -1 && parcelData[parcelSpi].conservationAreas.length > 0
    ).length
  }

  areaHasWarnings = () => {
    const { areaData } = this.props
    return (
      areaData &&
      (areaData.intNonUnpaid ||
        areaData.intCrownLand ||
        areaData.intNativeVegRegulationsArea ||
        areaData.intPrecincts ||
        (areaData.levyLand === 0 && !areaData.intPrecincts))
    )
  }

  renderWarning = (condition: boolean, areaType: string, warningMessage: string, warningContent: any) => {
    const { setInfoPopupState } = this.props

    return condition ? (
      <Callout type="warning">
        {warningMessage.replace('%REPLACE_TYPE%', areaType)}
        <ReadMoreButton label="Read more" onClick={() => setInfoPopupState(true, warningContent)} fontSize="16px" />
      </Callout>
    ) : null
  }

  renderParcelWarnings = (parcelSpi: string) => {
    const { parcelData } = this.props
    const areaType = 'parcel'
    return (
      <WarningContainer>
        {this.renderWarning(
          parcelData[parcelSpi].intNonUnpaid,
          areaType,
          warningMessages.nonUnpaid,
          ExistingPaymentWarning
        )}
        {this.renderWarning(
          parcelData[parcelSpi].intCrownLand,
          areaType,
          warningMessages.crownLand,
          CrownLandSelectedWarning
        )}
        {this.renderWarning(
          parcelData[parcelSpi].conservationAreas.length > 0,
          areaType,
          warningMessages.conservationArea,
          ConservationAreaWarning
        )}
        {this.renderWarning(
          parcelData[parcelSpi].intNativeVegRegulationsArea,
          areaType,
          warningMessages.nativeVegRegulationsArea,
          NvraAreaSelectedWarning
        )}
        {this.renderWarning(
          parcelData[parcelSpi].intPrecincts,
          areaType,
          warningMessages.twelvePrecincts,
          TwelvePrecinctsWarning
        )}
        {this.renderWarning(
          parcelData[parcelSpi].levyLand === 0 && !parcelData[parcelSpi].intPrecincts,
          areaType,
          warningMessages.outsideMsaLevyArea,
          OutsideMsaLevyAreaWarning
        )}
      </WarningContainer>
    )
  }

  renderParcelDetails = (parcelSpi: string) => {
    const { parcelData, rates } = this.props
    const data = parcelData[parcelSpi]
    return (
      <ParcelCard key={parcelSpi}>
        <div style={{ flex: 'auto' }}>
          <h6>{parcelSpi}</h6>
          <ParcelCardRow>
            <span style={{ fontWeight: 200 }}>{parcelData[parcelSpi].address}</span>
          </ParcelCardRow>
          {this.renderParcelWarnings(parcelSpi)}
          <div style={{ margin: '-10px' }}></div>
          <LevyLiability
            parcelSpi={parcelSpi}
            showDetailedTable
            filterZeros
            showHelp={false}
            titleFontSizePx="16"
            {...this.props}
          />
          <div style={{ margin: '-10px' }}></div>
          <ConservationAreas showHelp={false} titleFontSizePx="16" parcelSpi={parcelSpi} {...this.props} />
        </div>
        <ParcelEstimateDiv>
          <p style={{ marginBottom: '2px', textAlign: 'right' }}>Levy liability estimate:</p>
          <ParcelEstimateText>{getParcelEstimate(parcelSpi, parcelData)}</ParcelEstimateText>
        </ParcelEstimateDiv>
      </ParcelCard>
    )
  }

  renderAggregateWarning = (getNumberOfWarnings: any, warningMessage: any, warningContent: any) => {
    const { setInfoPopupState } = this.props
    const numberOfWarnings = getNumberOfWarnings()
    return numberOfWarnings > 0 ? (
      <Callout type="warning">
        {numberOfWarnings > 1 ? warningMessage.plural : warningMessage.singular}
        <ReadMoreButton label="Read more" onClick={() => setInfoPopupState(true, warningContent)} fontSize="16px" />
      </Callout>
    ) : null
  }

  renderAggregateWarnings = () => {
    return (
      <WarningContainer>
        {this.renderAggregateWarning(
          () => this.getNumberOfParcelWarnings('intNonUnpaid'),
          warningMessages.nonUnpaidAggregate,
          ExistingPaymentWarning
        )}
        {this.renderAggregateWarning(
          () => this.getNumberOfParcelWarnings('intCrownLand'),
          warningMessages.crownLandAggregate,
          CrownLandSelectedWarning
        )}
        {this.renderAggregateWarning(
          this.getNumberOfConservationAreaWarnings,
          warningMessages.ConservationAreaAggregate,
          ConservationAreaWarning
        )}
        {this.renderAggregateWarning(
          () => this.getNumberOfParcelWarnings('intNativeVegRegulationsArea'),
          warningMessages.nativeVegRegulationsAreaAggregate,
          NvraAreaSelectedWarning
        )}
        {this.renderAggregateWarning(
          () => this.getNumberOfParcelWarnings('intPrecincts'),
          warningMessages.twelvePrecinctsAggregate,
          TwelvePrecinctsWarning
        )}
        {this.renderAggregateWarning(
          this.getNumberOfOutsideMsaParcelWarnings,
          warningMessages.outsideMsaLevyAreaAggregate,
          OutsideMsaLevyAreaWarning
        )}
      </WarningContainer>
    )
  }

  renderAreaWarnings = () => {
    const { areaData } = this.props
    const areaType = 'area'

    return (
      <>
        <WarningContainer style={{ marginTop: '10px' }}>
          {this.renderWarning(areaData.intNonUnpaid, areaType, warningMessages.nonUnpaid, ExistingPaymentWarning)}
          {this.renderWarning(areaData.intCrownLand, areaType, warningMessages.crownLand, CrownLandSelectedWarning)}
          {this.renderWarning(
            areaData.conservationAreas.length > 0,
            areaType,
            warningMessages.conservationArea,
            ConservationAreaWarning
          )}
          {this.renderWarning(
            areaData.intNativeVegRegulationsArea,
            areaType,
            warningMessages.nativeVegRegulationsArea,
            NvraAreaSelectedWarning
          )}
          {this.renderWarning(areaData.intPrecincts, areaType, warningMessages.twelvePrecincts, TwelvePrecinctsWarning)}
          {this.renderWarning(
            areaData.levyLand === 0 && !areaData.intPrecincts,
            areaType,
            warningMessages.outsideMsaLevyArea,
            OutsideMsaLevyAreaWarning
          )}
        </WarningContainer>
      </>
    )
  }

  renderWarnings = () => {
    const { areaData } = this.props

    if (this.anyParcelHasWarnings() && !areaData) {
      return this.renderAggregateWarnings()
    } else if (this.areaHasWarnings()) {
      return this.renderAreaWarnings()
    } else {
      return null
    }
  }

  downloadObligationDetails = async () => {
    const { parcelsSelected, areaData } = this.props
    const { isDownloadingReport } = this.state

    if (isDownloadingReport) {
      return
    }

    let data
    if (!!parcelsSelected.length) {
      data = {
        spis: parcelsSelected
      }
    } else if (areaData && areaData.areaJson) {
      data = {
        json: areaData.areaJson
      }
    } else {
      throw new Error('Selected parcels or JSON featureset of uploaded area are required to generate report.')
    }

    const options = {
      method: 'POST',
      mode: 'cors' as RequestMode,
      cache: 'no-cache' as RequestCache,
      headers: {
        'Content-Type': 'application/json'
      },
      redirect: 'follow' as RequestRedirect,
      body: JSON.stringify(data)
    }

    const url = pdfReportFunctionAddress

    this.setState({ isDownloadingReport: true })
    let response
    try {
      response = await fetch(url, options)
    } catch (e) {
      console.log('Error generating Levy Liability Details report.')
      this.publish<OpenReportErrorDialogEvent>(messageTypes.onOpenReportErrorDialog)
      this.setState({ isDownloadingReport: false })
      return
    }

    if (response.status !== 200) {
      const responseJson = await response.json()
      console.log('Error generating Levy Liability Details report. Details:\n')
      console.log(responseJson)
      this.publish<OpenReportErrorDialogEvent>(messageTypes.onOpenReportErrorDialog)
    } else {
      window.open(response.url)
    }
    this.setState({ isDownloadingReport: false })
  }

  render() {
    const { parcelsSelected, onClose, parcelAggregates, areaData } = this.props
    const { isDownloadingReport } = this.state
    const formattedEstimateAmount = getTotalEstimate(parcelsSelected, parcelAggregates, areaData)

    return (
      <DetailsContainer>
        <Nav>
          <BackButton label="Back to search area" onClick={onClose} />
          <CloseButton onClick={onClose} />
        </Nav>
        <ScrollableContent>
          <Summary>
            <p style={{ marginBottom: '2px' }}>Total levy liability estimate:</p>
            <EstimateSummaryText>{formattedEstimateAmount}</EstimateSummaryText>
          </Summary>
          <Actions>
            <ActionButtonContainer>
              <FilledButton onClick={this.downloadObligationDetails} disabled={isDownloadingReport} href="" fullWidth>
                {isDownloadingReport ? (
                  <>
                    <div style={{ marginRight: '40px', display: 'inline-block' }}>Generating Report</div>
                    <div style={{ display: 'inline-block' }}>
                      <Spinner />
                    </div>
                  </>
                ) : (
                  'Download Levy Liability Details (PDF)'
                )}
              </FilledButton>
            </ActionButtonContainer>
            <ActionButtonContainer>
              <FilledButton href={getLinkWithPrefix('/pdfs/salvage-enquiry-form-(editable).pdf')} fullWidth useLink>
                Download Salvage Enquiry Form (PDF)
              </FilledButton>
            </ActionButtonContainer>
          </Actions>
          <Main>
            <div style={{ marginBottom: '40px' }}>
              <Callout type="info">
                <div style={{ marginTop: '10px' }}>
                  <ExpandoControl title="Show full terms" showPreview previewBackground={colours.lightPurpleBackground}>
                    <Terms />
                  </ExpandoControl>
                </div>
              </Callout>
            </div>
            {parcelsSelected.length ? (
              <SectionTitle title="Total selected parcels:">
                <SelectedParcelsSpan>{parcelsSelected.length}</SelectedParcelsSpan>
              </SectionTitle>
            ) : null}
            {this.renderWarnings()}
            <LevyLiability showDetailedTable {...this.props} />
            <ConservationAreas {...this.props} />
            <SalvageTranslocation {...this.props} />
            <NextSteps />
            {parcelsSelected.length ? (
              <ParcelBreakdown>
                <ExpandoControl title="Show details by parcel">
                  <>
                    <SectionTitle title="Parcel list"></SectionTitle>
                    {parcelsSelected.map(x => this.renderParcelDetails(x))}
                  </>
                </ExpandoControl>
              </ParcelBreakdown>
            ) : null}
          </Main>
        </ScrollableContent>
        <Footer></Footer>
      </DetailsContainer>
    )
  }
}

export default DetailsView
